import type { Component } from "solid-js";
import { createMemo, For, Show } from "solid-js";
import { useTranslation } from "../../../../state/i18n";
import { IconButton } from "../../../../components/button/Button";
import InlineMessage from "../../../../components/inlineMessage/InlineMessage";
import { MAX_FOLDERS } from "../../../../constants";
import ApiService from "../../../../services/ApiService";
import PermissionService from "../../../../services/PermissionService";
import { useCustomerState } from "../../../../state/customer";
import { QueryBoundary } from "../../../../utils/QueryBoundary";
import CreateFolderModal from "../../CreateFolderModal";
import { SidebarItem, SidebarMenu } from "./SidebarItem";
import SidebarBannerSection from "./SidebarBannerSection";
import { useModalManager } from "../../../../components/modal/ModalManager";
import SeparatorLine from "../../../../components/SeparatorLine";

const SidebarFolders: Component = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const foldersQuery = ApiService.getFolders();
  const hasReachedFolderLimit = createMemo(() =>
    foldersQuery.status === "success" ? foldersQuery.data.length >= MAX_FOLDERS : false,
  );

  const canManageDataSources = createMemo(() => PermissionService.canManageDataSources(customer.customerId));
  const canManagePermissions = createMemo(() => PermissionService.canManagePermissions(customer.customerId));

  return (
    <div class="flex min-h-full flex-col pt-4">
      <Show when={canManageDataSources() || canManagePermissions()}>
        <div class="flex flex-col gap-0.5 px-2">
          <Show when={canManageDataSources()}>
            <SidebarItem
              label={t(($) => $.SIDEBAR_ITEM_DATA_SOURCES_LABEL)}
              icon="database"
              href={`/customers/${customer.customerId}/data-sources`}
            />
          </Show>

          <Show when={canManagePermissions()}>
            <SidebarMenu
              label={t(($) => $.SIDEBAR_SECTION_USER_MANAGEMENT_LABEL)}
              icon="users"
              items={[
                { label: t(($) => $.SIDEBAR_ITEM_USERS_LABEL), href: `/customers/${customer.customerId}/users` },
                { label: t(($) => $.SIDEBAR_ITEM_GROUPS_LABEL), href: `/customers/${customer.customerId}/groups` },
                { label: t(($) => $.SIDEBAR_ITEM_ROLES_LABEL), href: `/customers/${customer.customerId}/roles` },
              ]}
            />
          </Show>

          <SeparatorLine class="my-6" />
        </div>
      </Show>

      <div class="ml-2.5 flex min-h-7.5 shrink-0 items-center px-2">
        <h3 class="text-base-semibold text-secondary">{t(($) => $.SIDEBAR_FOLDERS_LIST_TITLE)}</h3>
        <Show when={PermissionService.canCreateFolder(customer.customerId) && !hasReachedFolderLimit()}>
          <IconButton
            icon="plus"
            size="small"
            variant="secondary"
            aria-label={t(($) => $.SIDEBAR_FOLDERS_LIST_CREATE_ACTION_ARIA_LABEL)}
            class="ml-auto"
            onClick={() =>
              open({
                content: () => <CreateFolderModal customerId={customer.customerId} />,
              })
            }
          />
        </Show>
      </div>
      <Show when={hasReachedFolderLimit()}>
        <div class="px-2 pt-3">
          <InlineMessage
            variant="warning"
            label={t(($) => $.SIDEBAR_FOLDERS_LIST_LIMIT_REACHED, { folderLimit: MAX_FOLDERS })}
          />
        </div>
      </Show>
      <QueryBoundary>
        <div class="mt-3 overflow-y-auto px-2 py-1">
          <For each={foldersQuery.data ?? []} fallback={<NoFolders />}>
            {(folder) => (
              <SidebarItem
                label={folder.meta.name}
                icon={folder.meta.icon}
                href={`/customers/${customer.customerId}/folders/${folder.id}`}
              />
            )}
          </For>
        </div>
      </QueryBoundary>
      <SidebarBannerSection classList={{ "px-2": true }} />
    </div>
  );
};

const NoFolders: Component = () => {
  const { t } = useTranslation();

  return (
    <div class="ml-2.5 py-4 text-medium-regular text-secondary">{t(($) => $.SIDEBAR_FOLDERS_LIST_EMPTY_TITLE)}</div>
  );
};

export default SidebarFolders;
